import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { AuthLayout } from "@/components/Layout";
import { AuthCard } from "@/components/Card";
import { Alert } from "@/components/Alert";
import { Logo } from "@/components/Logo";

export default function ConfirmEmail() {
  const nav = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function settle() {
      const { data, error: sessionError } = await supabase.auth.getSession();
      if (cancelled) return;
      if (sessionError) {
        setError(sessionError.message);
        return;
      }
      if (!data.session) {
        setError("Your confirmation link is invalid or has expired.");
        return;
      }

      const user = data.session.user;
      const meta = user.user_metadata as { first_name?: string; last_name?: string };

      const { data: existing } = await supabase
        .from("patients")
        .select("id")
        .eq("user_id", user.id)
        .maybeSingle();

      if (!existing) {
        await supabase.from("patients").insert({
          user_id: user.id,
          first_name: meta.first_name ?? "Patient",
          last_name: meta.last_name ?? "",
        });
      }

      // Whether the patient row is brand new or already existed, sync with
      // Stripe so the billing page reflects any subscription created during
      // the intake. Idempotent — calling this twice with no Stripe customer
      // is a no-op.
      await supabase.functions.invoke("patient-link-stripe", { body: {} }).then(
        () => null,
        (err: unknown) => console.warn("[ConfirmEmail] patient-link-stripe failed:", err),
      );

      nav("/dashboard", { replace: true });
    }

    settle();
    return () => {
      cancelled = true;
    };
  }, [nav]);

  return (
    <AuthLayout>
      <AuthCard>
        <div className="mb-6 flex justify-center"><Logo size="lg" href="https://fortehealth.co" /></div>
        <h1 className="font-serif text-[25px] font-normal text-fh-ink text-center mb-1.5">
          {error ? "Something went wrong" : "Confirming…"}
        </h1>
        {error ? (
          <div className="flex flex-col gap-3 mt-4">
            <Alert tone="error">{error}</Alert>
            <Link to="/login" className="text-center text-[13px] font-semibold text-fh-green hover:text-fh-green-dark">
              Back to sign in
            </Link>
          </div>
        ) : (
          <p className="text-fh-gray text-center">Finishing up — one moment.</p>
        )}
      </AuthCard>
    </AuthLayout>
  );
}
